import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Building2, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { listEmpresas, type Empresa } from '@/services/api/empresas';
import { routes } from '@/lib/routes';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { BrandMark } from '@/components/common/BrandMark';

/**
 * Pantalla de selección de empresa — solo para APEX.
 * Si hay 1 empresa, redirige directo.
 * Si hay N, grid de cards clickeables.
 */
export function SelectEmpresaPage() {
  const { isApex } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState<Empresa[] | null>(null);

  useEffect(() => {
    listEmpresas().then((res) => setEmpresas(res.ok ? res.data : []));
  }, []);

  if (!isApex) return <Navigate to="/" replace />;
  if (!empresas) return <LoadingScreen message="Cargando empresas…" />;

  if (empresas.length === 1 && empresas[0]) {
    return <Navigate to={routes.empresa.dashboard(empresas[0].id)} replace />;
  }

  return (
    <div className="min-h-screen px-6 py-10">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 flex items-center justify-between">
          <BrandMark size={64} />
          <p className="kicker">Seleccionar empresa</p>
        </div>

        <div className="card-premium p-8">
          <h1 className="mb-2 text-2xl font-extrabold tracking-tight">
            ¿Sobre qué empresa querés trabajar?
          </h1>
          <p className="mb-6 text-sm text-neutral-muted">
            Como APEX ves todas. Elegí una para entrar a su panel.
          </p>

          {empresas.length === 0 ? (
            <p className="rounded-md bg-orange/10 p-4 text-sm text-orange">
              Todavía no hay empresas cargadas. Creá la primera desde Configuración.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {empresas.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => navigate(routes.empresa.dashboard(e.id))}
                  className="group flex items-center gap-4 rounded-md border border-surface-line bg-white p-4 text-left transition hover:border-blue/40 hover:shadow-premium-soft"
                >
                  <div className="grid h-12 w-12 flex-none place-items-center rounded-md bg-blue-10 text-blue">
                    <Building2 size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-neutral-ink">{e.nombre}</p>
                    <p className="truncate text-xs text-neutral-muted">CUIT {e.cuit}</p>
                  </div>
                  <ChevronRight
                    size={18}
                    className="flex-none text-neutral-muted transition group-hover:translate-x-0.5 group-hover:text-blue"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
